/**
 * Comparing two views of the program: the default branch and any baselines (issue
 * #97, phase E).
 *
 * "What changed in HEAD relative to BASE", and the answer is the very list the
 * Version Control screen draws for a branch - the server returns the same shape -
 * so it is drawn by the same ChangeList and exported by the same menu. Nothing about
 * the comparison is computed here.
 *
 * A baseline has to have been opened (read into its read-only copy) before it can be
 * compared. The server says so with a 409 that names the tag; that is not a fault,
 * so it is shown as an offer to open it, and once it is open the comparison runs
 * again by itself.
 *
 * Every refusal (unknown tag, the same side twice) is shown in the server's words.
 * Tag names are plain text throughout.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { api } from '../../api'
import type { ApiError } from '../../api'
import type { ModelChanges } from '../../schema'
import { MONOSPACE } from '../../theme'
import BaselineOpening from './BaselineOpening'
import ChangeList from './ChangeList'
import ChangesExportMenu from './ChangesExportMenu'
import type { OpeningBaselines } from './useOpenBaseline'

type Outcome =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'changes'; base: string; head: string; changes: ModelChanges }
  | { kind: 'not-opened'; base: string; head: string; tag: string; detail: string }
  | { kind: 'refused'; message: string }

export default function BaselineCompare({
  slug,
  target,
  names,
  initial,
  opening,
  onOpened,
  onClose,
}: {
  slug: string
  /** The default branch's name: one of the two things a side can be. */
  target: string
  /** The baselines a side can be. */
  names: string[]
  /** The sides the panel starts with. */
  initial: { base: string; head: string }
  opening: OpeningBaselines
  /** A baseline was opened from here, so the list's `opened` flags are out of date. */
  onOpened: () => void
  onClose: () => void
}) {
  const [base, setBase] = useState(initial.base)
  const [head, setHead] = useState(initial.head)
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' })

  const sides = [target, ...names]

  const run = async (chosen: { base: string; head: string }) => {
    setOutcome({ kind: 'loading' })
    try {
      const answer = await api.compareBaselines(slug, chosen)
      if ('notOpened' in answer) {
        setOutcome({ kind: 'not-opened', ...chosen, ...answer.notOpened })
      } else {
        setOutcome({ kind: 'changes', ...chosen, changes: answer.changes })
      }
    } catch (refused) {
      setOutcome({ kind: 'refused', message: (refused as ApiError).message || 'Could not compare' })
    }
  }

  const openThenRun = async (pending: { base: string; head: string; tag: string }) => {
    const status = await opening.open(pending.tag)
    if (status?.state === 'ready') {
      onOpened()
      await run({ base: pending.base, head: pending.head })
    }
  }

  const select = (label: string, value: string, onChange: (next: string) => void) => (
    <TextField
      label={label}
      select
      size="small"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      slotProps={{ select: { native: true } }}
      sx={{ minWidth: 260 }}
    >
      {sides.map((side) => (
        <option key={side} value={side}>
          {side}
        </option>
      ))}
    </TextField>
  )

  return (
    <Paper sx={{ p: 2 }} data-testid="baseline-compare">
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, alignItems: 'center' }}>
        <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
          Compare
        </Typography>
        <Button size="small" onClick={onClose}>
          Close
        </Button>
      </Stack>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1.5 }}>
        {select('Base', base, setBase)}
        {select('Head', head, setHead)}
        <Button
          variant="contained"
          onClick={() => void run({ base, head })}
          disabled={outcome.kind === 'loading'}
        >
          Run comparison
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Shows what changed in the head relative to the base.
      </Typography>

      {outcome.kind === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} aria-label="Comparing" />
        </Box>
      ) : null}

      {outcome.kind === 'refused' ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {outcome.message}
        </Alert>
      ) : null}

      {outcome.kind === 'not-opened' ? (
        <Box sx={{ mt: 1.5 }}>
          <Alert
            severity="info"
            action={
              opening.statuses[outcome.tag]?.state === 'opening' ? undefined : (
                <Button color="inherit" size="small" onClick={() => void openThenRun(outcome)}>
                  {`Open ${outcome.tag}`}
                </Button>
              )
            }
          >
            {outcome.detail || `${outcome.tag} has not been opened yet.`}
          </Alert>
          {opening.statuses[outcome.tag] ? (
            <BaselineOpening status={opening.statuses[outcome.tag]} />
          ) : null}
        </Box>
      ) : null}

      {outcome.kind === 'changes' ? (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              What changed in{' '}
              <Box component="span" sx={{ fontFamily: MONOSPACE }}>
                {outcome.head}
              </Box>{' '}
              since{' '}
              <Box component="span" sx={{ fontFamily: MONOSPACE }}>
                {outcome.base}
              </Box>
            </Typography>
            <ChangesExportMenu
              urlFor={(fmt, part) =>
                api.compareBaselinesExportUrl(slug, {
                  base: outcome.base,
                  head: outcome.head,
                  fmt,
                  part,
                })
              }
            />
          </Stack>
          <ChangeList changes={outcome.changes} loading={false} error={null} target={outcome.base} />
        </Box>
      ) : null}
    </Paper>
  )
}
